import type { Theme } from "@mui/material/styles";
import { colors } from "../utils/colors";

export const getNavigationDrawerStyles = (theme: Theme) => ({
  drawer: {
    "& .MuiDrawer-paper": {
      width: "21.875rem",
      backgroundColor: "#f8f7fc",
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      paddingX: theme.spacing(2),
      overflowX: "hidden",
      scrollbarGutter: "stable",
      scrollbarWidth: "thin",
      "&::-webkit-scrollbar": {
        width: "0.5rem",
      },
      "&::-webkit-scrollbar-track": {
        boxShadow: "0 0.125rem 0.125rem 0 rgba(0, 0, 0, 0.06) inset",
        backgroundColor: "#DEDEDE",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#BEBEBE",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: "#BEBEBE",
      },
    },
  },
  drawerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  drawerTitle: {
    fontWeight: "600",
    fontSize: "1.25rem",
  },
  drawerCloseButton: {
    color: theme.palette.text.primary,
  },
});

export const getDashboardNavigationAccordionStyles = (theme: Theme) => ({
  rootBox: {
    width: "100%",
    paddingBottom: "2rem",
  },
  accordion: {
    marginBottom: "0.3125rem",
    border: "none",
    boxShadow: "none",
  },
  accordionSummary: {
    marginBottom: "0.3125rem",
    border: "none",
    height: "4.25rem",
  },
  accordionSummaryContent: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
  },
  sectionTitle: {
    width: "100%",
    flexShrink: 0,
    fontSize: "1rem",
    fontWeight: "600",
  },
  sectionSubtitle: {
    width: "100%",
    flexShrink: 0,
    fontSize: "0.875rem",
    fontWeight: "500",
  },
  accordionDetails: {
    padding: 0,
  },
  divider: {
    height: "0.25rem",
    backgroundColor: "#f8f7fc",
    flexGrow: 1,
  },
  dashboardButton: {
    flexDirection: "column",
    textTransform: "capitalize",
    alignItems: "flex-start",
    textAlign: "left",
    width: "100%",
    minHeight: "3rem",
    paddingLeft: "1rem",
    paddingTop: "0.75rem",
    paddingBottom: "0.75rem",
    border: "none",
    borderRadius: "0px",
    backgroundColor: "#E4E4E4",
    color: theme.palette.text.primary,
    textDecoration: "none",
    ":hover": {
      bgcolor: colors.dashboard_nav_child_surface,
      textDecoration: "none",
    },
  },
  dashboardButtonTitle: {
    fontSize: "1rem",
    fontWeight: "400",
  },
});

export type DashboardChildSectionNavVariant = "panel" | "card";

export const getDashboardChildSectionNavStyles = (
  theme: Theme,
  variant: DashboardChildSectionNavVariant = "panel",
) => {
  const isCard = variant === "card";
  const parentSurface = isCard
    ? theme.palette.grey[50]
    : colors.dashboard_nav_parent_surface;
  const childSurface = isCard
    ? theme.palette.grey[100]
    : colors.dashboard_nav_child_surface;

  const cardDashboardRow = isCard
    ? {
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box" as const,
        minHeight: "3.375rem",
        borderRadius: "8px",
        backgroundColor: parentSurface,
        px: 2,
        py: 1.25,
      }
    : {};

  return {
    root: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      alignSelf: "stretch",
      ...(isCard
        ? { backgroundColor: "transparent" }
        : { backgroundColor: parentSurface }),
    },
    accordion: {
      margin: 0,
      border: "none",
      boxShadow: "none",
      width: "100%",
      maxWidth: "100%",
      alignSelf: "stretch",
      backgroundColor: parentSurface,
      "&:before": { display: "none" },
      "&.Mui-expanded": { margin: 0 },
      ...(isCard && {
        borderRadius: "8px",
      }),
    },
    accordionSummary: {
      marginBottom: 0,
      border: "none",
      backgroundColor: parentSurface,
      ...cardDashboardRow,
      ...(isCard
        ? {
            padding: theme.spacing(1.25, 2),
            "&.Mui-expanded": {
              minHeight: "3.375rem !important",
              borderRadius: "8px 8px 0 0",
            },
            "&.MuiAccordionSummary-root": {
              width: "100%",
              maxWidth: "100%",
              padding: theme.spacing(1.25, 2),
            },
          }
        : {
            height: "4.25rem",
            minHeight: "4.25rem",
            paddingLeft: "1.5rem",
            "&.Mui-expanded": {
              minHeight: "4.25rem",
            },
          }),
      "& .MuiAccordionSummary-content": {
        my: 0,
        margin: 0,
        flexGrow: 1,
        width: "100%",
        "&.Mui-expanded": { my: 0 },
      },
      "&:hover": {
        backgroundColor: isCard
          ? theme.palette.grey[200]
          : colors.dashboard_nav_child_surface,
      },
    },
    accordionSummaryContent: {
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      width: "100%",
    },
    sectionTitle: {
      width: "100%",
      flexShrink: 0,
      fontSize: "1rem",
      fontWeight: "400",
      ...(isCard ? { m: 0 } : { paddingLeft: "1.5rem" }),
    },
    sectionSubtitle: {
      width: "100%",
      flexShrink: 0,
      fontSize: "0.875rem",
      fontWeight: isCard ? 300 : "400",
      color: theme.palette.text.secondary,
      lineHeight: isCard ? 1.2 : undefined,
      ...(isCard ? { m: 0 } : { paddingLeft: "1.5rem" }),
    },
    accordionDetails: {
      padding: 0,
      width: "100%",
      backgroundColor: parentSurface,
    },
    childNavList: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      width: "100%",
      boxSizing: "border-box",
      py: 0.5,
      pb: 1,
      backgroundColor: childSurface,
      ...(isCard
        ? {
            px: 2,
            borderRadius: "0 0 8px 8px",
          }
        : {
            pl: "1.5rem",
            pr: "6px",
          }),
    },
  };
};
